import React, { PureComponent } from 'react';
import sleep from 'sleep-promise';

import SendScreen from './SendScreen';
import { getUnitName, getUnitWp, getsIdsMoxspotNames, getDeliveryType, countForReload, h2r } from '../vodjs/lib';
import { elasticLogTaskAction, elasticLogEvent, elasticLogError } from '../vodjs/ElasticLog';
import { areSame, makeId, clearFetch } from '../vodjs/Utils';
import { getConfig } from '../utils/configUtils';
import RequestScreen from './RequestScreen';
import ConfirmRequestScreen from './ConfirmRequestScreen';
import TaskRequest from '../vodjs/TaskRequest';
import { makeTaskDecorator } from '../vodjs/Task';
import { ONE_TO_MANY, TASK_TAG, REQUIRED_ROLE, TASK_TYPE } from '../constants/Tasks';
import { ticksToDateCode } from '../utils/commonUtils';
import { KIOSK_SETTINGS } from '../constants/KioskSettings';
import { getSettingValue } from '../utils/kioskSettingsUtils';
import { getShared } from '../vodjs/Utils';
import {getUseFirestore} from '../constants/Meta';

export default class StandardSendScreen extends PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      confirmVisible: false,
      isRestricted: false,
      isCodeRequired: false,
      showRestricted: false,
      dropoffs: [],
      isLoading: false,
      endGoals: [],
    };
  }

  componentDidMount() {
    if (getConfig().kioskSettings) {
      this.updateKioskSettings();
      this.setEndGoals();
    }
  }

  componentDidUpdate(prevProps) {
    if (
      getConfig().kioskSettings &&
      (!areSame(prevProps.kioskSettings, this.props.kioskSettings) ||
        !areSame(prevProps.allKioskSettings, this.props.allKioskSettings))
    ) {
      this.updateKioskSettings();
      this.setEndGoals();
    }
  }

  updateKioskSettings() {
    const automaticRestrictedDelivery = getSettingValue(
      this.props.kioskSettings,
      KIOSK_SETTINGS.AutomaticRestrictedDelivery,
    );
    this.setState({ isRestricted: automaticRestrictedDelivery });

    const enableRestrictedDelivery = getSettingValue(
      this.props.kioskSettings,
      KIOSK_SETTINGS.RequestRestrictedDelivery,
    );
    this.setState({ showRestricted: enableRestrictedDelivery });
  }


  setEndGoals() {
    const unitName = getUnitName();
    let endGoals = this.props.getEnabledKioskFriendlyNames().filter(friendlyName => friendlyName != unitName);
    if(!endGoals.length) endGoals.push(this.noKioskText());
    endGoals.sort();
    this.setState({ endGoals });
  }


  noKioskText() {
    const supportNumber = getConfig()?.site?.supportNumber;
    const noKioskText = `No locations are available. Please call Moxi support ${ supportNumber ? `(${supportNumber}) ` : '' }for help.`;
    return noKioskText;
  }


  generateTaskRequestArgs(index) {
    let taskRequestArgs = {
      start_is_room: false,
      end_is_room: false,
    };

    if (!this.state.dropoffs || index < 0 || index >= this.state.dropoffs.length) {
      return taskRequestArgs;
    }

    taskRequestArgs.start_goal = h2r(this.state.dropoffs[index].startGoal);
    taskRequestArgs.end_goal = h2r(this.state.dropoffs[index].endGoal);

    return taskRequestArgs;
  }

  generateTaskRequestData(index) {
    const data = {};
    let note = (this.state.dropoffs[index].note || '').trim();
    if (this.state.dropoffs.length > 1) note = note + '\n';
    data.sender_note = note;

    return data;
  }

  async createTask(index) {
    let appTaskRequestId = makeId(4);
    const userTaskRequestTime = new Date().toISOString();
    let taskRequestArgs = this.generateTaskRequestArgs(index);
    let { start_goal, end_goal } = taskRequestArgs;
    const isUFB = true; //used solely for logging
    elasticLogTaskAction({ what: 'taskRequested', appTaskRequestId, userTaskRequestTime, start_goal, end_goal, isUFB });

    let exit = (logMessage, userMessage) => {
      elasticLogError({ what: 'createTask', appTaskRequestId, start_goal, end_goal, message: logMessage });
      this.setState({ isLoading: false });
      this.props.closeModal({ title: 'Error requesting task', message: userMessage ?? logMessage });
    };

    if (!start_goal || !end_goal) {
      const supportNumber = getConfig()?.site?.supportNumber;
      const logMessage = `request: error in task request args. dropoffs: ${JSON.stringify(this.state.dropoffs)}`;
      const userMessage = `Try again, or contact Moxi support${
        supportNumber ? ` (${supportNumber})` : ''
      } for assistance.`;
      return exit(logMessage, userMessage);
    }

    let response;

    try {
      response = await TaskRequest.request(
        getUnitWp(),
        getDeliveryType(),
        this.state.isRestricted ? REQUIRED_ROLE.RESTRICTED : REQUIRED_ROLE.BASIC,
        taskRequestArgs,
        this.generateTaskRequestData(index),
        false,
      );

      const uuid = response.task_uuid;

      elasticLogEvent({
        what: 'taskCreated',
        appTaskRequestId,
        userTaskRequestTime,
        taskUuid: uuid,
        start_goal,
        end_goal,
        isUFB,
      });
    }
    catch (error) {
      return exit('request: ' + error.message);
    }

    const taskUuid = response.task_uuid;
    return taskUuid;
  }

  //mfd unused
  // async create1MBatchTasks(payload) {
  //   let appTaskRequestId = makeId(4);
  //   let response, uuids;
  //   const userTaskRequestTime = new Date().toISOString();
  //   const pickup = payload.tasks[0].startLocations[0];
  //   const dropoffs = payload.tasks.map((t) => t.endLocations[0]);
  //   const stops = [pickup, ...dropoffs];

  //   elasticLogTaskAction({ what: 'batchTasksRequested', appTaskRequestId, stops, userTaskRequestTime });

  //   let exit = (message) => {
  //     elasticLogError({ what: 'createBatchTasks', appTaskRequestId, message });
  //     this.setState({ isLoading: false });
  //     this.props.closeModal({ title: 'Error requesting batch task', message });
  //   };

  //   try {
  //     response = await TaskRequest.batchRequest(payload);
  //     uuids = response.map((t) => t.id);
  //   }
  //   catch (error) {
  //     return exit('createBatchTasks: ' + error.message);
  //   }

  //   elasticLogEvent({ what: 'batchTasksCreated', appTaskRequestId, userTaskRequestTime, uuids, stops });
  //   return uuids;
  // }

  async createTasks() {

    if(this.creatingTicks) {
      let secsAgo = Math.round((Date.now() - this.creatingTicks) / 100) / 10;
      elasticLogEvent({ what: 'reentryCreateTasks', msg:`createTasks redundant attempt (${secsAgo}s ago}`, origTicks:this.creatingTicks });
      return;
    }
    this.creatingTicks = Date.now(); //prevent reentry
    try {

      let createdUuids = [];
      const { dropoffs, isRestricted } = this.state;
      const batchTasksPayload = {
        batchType: TASK_TAG.BATCH_1M,
        tasks: [],
        createdForId: getUnitWp(),
      };

      this.setState({ isLoading: true });

      if(!getUseFirestore()) {
        //have mbotSvc create the tasks/batch

        let exit = ({start_goal, end_goal, logMessage, userMessage}) => {
          elasticLogError({ what: 'createTask', start_goal, end_goal, message: logMessage });
          this.setState({ isLoading: false });
          this.props.closeModal({ title: 'Error requesting task', message: userMessage ?? logMessage });
        };

        let tasksData = [];
        for (const index of dropoffs.keys()) {
          let taskLevelArgs = this.generateTaskRequestArgs(index);
          let { start_goal, end_goal } = taskLevelArgs;

          if (!start_goal || !end_goal) {
            const supportNumber = getConfig()?.site?.supportNumber;
            const logMessage = `request: error in task request args. dropoffs: ${JSON.stringify(this.state.dropoffs)}`;
            const userMessage = `Try again, or contact Moxi support${ supportNumber ? ` (${supportNumber})` : '' } for assistance.`;
            return exit({start_goal, end_goal, logMessage, userMessage});
          }

          let taskData = this.generateTaskRequestData(index);
          let senderNote = taskData.sender_note;

          tasksData.push({
            requester_id: getUnitWp(),
            task_type: getDeliveryType(),
            startGoal:taskLevelArgs.start_goal,
            endGoal:taskLevelArgs.end_goal,
            senderNote
          });
        }

        let meta = {
          batchType: dropoffs.length > 1 ? ONE_TO_MANY : undefined,
          restricted: getConfig().secureDelivery ? !!isRestricted : false,
          code_required: !!(this.props.autoPrioConfig?.pin_code_phase1 && this.state.isCodeRequired)
        };

        const userTaskRequestTime = new Date().toISOString();

        let options = {
          method: 'POST',
          headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
          body: JSON.stringify({tasksData, meta})
        };

        let successJson;
        await clearFetch(`${getConfig().uiSimServer}/createTasks`, options, {
          success: (json) => successJson = json,
          bad: ({ logStr }) => {
            elasticLogError({ what: 'useHPErr', msg: `Error asking server whether to use hallpass: ` + logStr });


            let msg = `Error requesting ${dropoffs.length > 1 ? 'tasks' : 'task'}: ${logStr}`;
            elasticLogError({ what: 'createTask', msg });
            this.setState({ isLoading: false });
            this.props.closeModal({ title: 'Error requesting task', message: msg });
          }
        });
        if(!successJson) return;

        let {uuids} = successJson;
        uuids = uuids || [];
        for(let i = 0; i < uuids.length; ++i) {
          let taskUuid = uuids[i];
          let taskData = tasksData[i];
          let {start_goal, end_goal} = taskData.taskArgs;
          elasticLogEvent({ what: 'taskCreated', userTaskRequestTime, taskUuid, start_goal, end_goal, isUFB:true });
        }
      }
      else {
        //create the tasks and decorate them from here on the kiosk

        for (const index of dropoffs.keys()) {
          const taskUuid = await this.createTask(index);
          if (taskUuid) createdUuids.push(taskUuid);
        }

        if (getConfig().secureDelivery || dropoffs.length > 1) {
          const batchId = ticksToDateCode(Date.now());
          try {
            let start = Date.now();
            const decorator = makeTaskDecorator((uuid) => this.props.tasks.find((t) => t.uuid === uuid));

            for (const [index, uuid] of createdUuids.entries()) {
              const data = {};
              if (getConfig().secureDelivery) {
                data.restricted = !!isRestricted; // undefined will cause firestore errs, so !! to force to boolean
              }
              if (dropoffs.length > 1) {
                let stops = [h2r(dropoffs[0].startGoal), ...dropoffs.map((d) => h2r(d.endGoal))]; //specific to ONE_TO_MANY

                data.batch = {
                  batchType: ONE_TO_MANY,
                  id: batchId,
                  index,
                  total: dropoffs.length,
                  stops,
                  uuids: createdUuids,
                };
              }

              if (this.props.autoPrioConfig?.pin_code_phase1 && this.state.isCodeRequired) {
                data.code_required = this.state.isCodeRequired;
              }

              decorator.add({ uuid, data });
            }

            decorator.allTaskUuidsAdded();

            while (Date.now() < start + 1000 * 10) {
              if (decorator.isDone()) break;
              await sleep(250);
            }
          }
          catch (e) {
            elasticLogError({ what: 'decorateTask', errorStr: String(e.message) });
          }
        }    
      }

      this.setState({ isLoading: false, dropoffs: [], isRestricted: false, confirmVisible: false });
      this.props.closeModal();
    }
    finally {
      delete this.creatingTicks;
    }
  }

  generateNotes() {
    if (this.state.dropoffs.length > 1) {
      const combinedNotes = this.state.dropoffs.map((d) => (
        <div className="details__multi-note" key={`note-${d.id}`}>
          <strong>{d.endGoal}: </strong>
          {d.note ? d.note.trim() : <span className="no-notes">No notes</span>}
        </div>
      ));
      return <>{combinedNotes}</>;
    }
    return this.state.dropoffs[0].note.trim() || 'No notes';
  }

  render() {
    if (getConfig().userFacingBatching) {   //coerce to true to debug ufb

      if (this.state.confirmVisible) {
        return (
          <ConfirmRequestScreen
            requester={getUnitName()}
            note={this.generateNotes()}
            pickups={[getUnitName()]}
            dropoffs={this.state.dropoffs.map((d) => d.endGoal)}
            clickConfirm={() => this.createTasks()}
            clickCancel={() => this.setState({ confirmVisible: false })}
            tapToReload={() => countForReload({ label: 'ConfirmRequest' })}
            hideDetails={this.props.taskAttrs.failedToPullTask}
            isRestricted={this.state.isRestricted}
            isLoading={this.state.isLoading}
            isCodeRequired={this.state.isCodeRequired}
          />
        );
      }

      return (
        <RequestScreen
          disableStartGoalSelection={true}
          endGoals={this.state.endGoals}
          defaultStartGoal={getUnitName()}
          closeModal={this.props.closeModal}

          // tasks={this.props.tasks}

          clickRequest={() => this.setState({ confirmVisible: true })}
          dropoffs={this.state.dropoffs}
          setDropoffs={(value) => this.setState({ dropoffs: value })}
          isRestricted={this.state.isRestricted}
          setRestricted={(value) => {
            if (!value) this.setState({ isCodeRequired: false });
            this.setState({ isRestricted: value });
          }}
          isCodeRequired={this.state.isCodeRequired}
          setPinRequired={(value) => this.setState({ isCodeRequired: value })}
          showRestricted={this.state.showRestricted}
          noKioskText={this.noKioskText()}
          autoPrioConfig={this.props.autoPrioConfig} 
          getOpenHoursInfo={stationCode => this.props.getOpenHoursInfo(stationCode)}
          isTest={false} // only requests originating from mbot can be marked as test
        />
      );
    }
    else {
      //mfd; we have ufb everywhere, right?
      return (
        <SendScreen
          title={`Order a delivery from ${getUnitName()} to`}
          disableStartGoalSelection={true}
          endGoals={this.state.endGoals}
          defaultStartGoal={getUnitName()}
          closeModal={this.props.closeModal}
          tasks={this.props.tasks}
          isRestricted={this.state.isRestricted}
          setRestricted={(value) => this.setState({ isRestricted: value })}
          showRestricted={this.state.showRestricted}
          noKioskText={this.noKioskText()}
          getOpenHoursInfo={stationCode => this.props.getOpenHoursInfo(stationCode)}
        />
      );
    }
  }
}

// Note: This is needed because publishing to expo runs minify, which changes the class names. In particular, classes
//       can end up with the same name which breaks the screen transition logic.
StandardSendScreen.viewName = 'StandardSendScreen';
